import React, {useState, useEffect} from 'react'
import {Box, Typography, Button, Chip} from '@mui/material'
import {useNavigate} from 'react-router-dom'
import {subDays} from 'date-fns'
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined'
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import withLayout from '../../hocs/Layout'
import PageTitle from '../../common/PageTitle'
import {pageContainerSx} from '../../styles/patterns'
import {getMerchant, isAuthorized} from '../../helpers/ReduxHelpers'
import {ROLE_COMBINATIONS} from '../../../routes/routes.config'
import {postGeneric} from '../../common/constants'
import {FONT, SIZE, WEIGHT} from '../../styles/foundation/typography'
import {NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, SUCCESS, withOpacity} from '../../styles/foundation/colors'
import {SHADOW} from '../../styles/foundation/effects'

const cardSx = {
  bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '14px',
  boxShadow: SHADOW.card, p: 2.5, display: 'flex', flexDirection: 'column', height: '100%',
}
const gridSx = {display: 'grid', gridTemplateColumns: {xs: '1fr', md: 'repeat(2, 1fr)'}, gap: 2.5}

function SectionHeader({title, sub}) {
  return (
    <Box sx={{mt: 3, mb: 1.5}}>
      <Typography sx={{fontFamily: FONT, fontSize: '11px', fontWeight: WEIGHT.extra, letterSpacing: '0.07em', textTransform: 'uppercase', color: SUBTLE}}>{title}</Typography>
      {sub && <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: '#93a0b0', mt: 0.3}}>{sub}</Typography>}
    </Box>
  )
}

// A service card: coloured icon, title + status chip, plain description, a body, and one
// obvious full-width call-to-action so a non-technical merchant always knows the next step.
function ServiceCard({icon, accent = PRIMARY_BLUE, title, status, desc, children, ctaLabel, onCta}) {
  return (
    <Box sx={cardSx}>
      <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1.5}}>
        <Box sx={{width: 42, height: 42, borderRadius: '12px', flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: withOpacity(accent, 0.12), color: accent}}>
          {icon}
        </Box>
        <Box sx={{flex: 1, minWidth: 0}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
            <Typography sx={{fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY}}>{title}</Typography>
            {status}
          </Box>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.4, lineHeight: 1.45}}>{desc}</Typography>
        </Box>
      </Box>
      <Box sx={{flex: 1, mt: children ? 1.5 : 0}}>{children}</Box>
      {ctaLabel && (
        <Button fullWidth variant='outlined' endIcon={<ArrowForwardIcon sx={{fontSize: 16}} />} onClick={onCta}
          sx={{mt: 2, textTransform: 'none', fontWeight: WEIGHT.semibold, borderRadius: '10px', py: 0.85}}>
          {ctaLabel}
        </Button>
      )}
    </Box>
  )
}

// Big-number stat used inside the NDR Calling card.
function Stat({value, label, color = NAVY}) {
  return (
    <Box>
      <Typography sx={{fontFamily: FONT, fontSize: '22px', fontWeight: WEIGHT.extra, lineHeight: 1, color}}>{value}</Typography>
      <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em'}}>{label}</Typography>
    </Box>
  )
}

// A short bulleted "what you get" list for cards that don't show live figures.
function Bullets({items}) {
  return (
    <Box sx={{mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75}}>
      {items.map((t) => (
        <Box key={t} sx={{display: 'flex', alignItems: 'flex-start', gap: 0.75}}>
          <CheckCircleRoundedIcon sx={{fontSize: 15, color: SUCCESS, mt: '1px', flex: 'none'}} />
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: NAVY, lineHeight: 1.45}}>{t}</Typography>
        </Box>
      ))}
    </Box>
  )
}

const chip = (label, color) => <Chip size='small' label={label} sx={{height: 20, fontFamily: FONT, fontSize: '10px', fontWeight: WEIGHT.bold, bgcolor: withOpacity(color, 0.14), color}} />

function VasDashboard() {
  const navigate = useNavigate()
  const merchant = getMerchant()
  const merchantId = merchant?.id

  const [callAnalytics, setCallAnalytics] = useState(null)
  const [callLoading, setCallLoading] = useState(true)

  useEffect(() => {
    if (!merchantId) { setCallLoading(false); return }
    const today = new Date()
    const thirtyDaysAgo = subDays(today, 30)
    const iso = (d) => d.toISOString().slice(0, 10)
    setCallLoading(true)
    postGeneric('intervention/calls/analytics', {
      type: 'NDR', merchantOids: [merchantId], dateRange: {from: iso(thirtyDaysAgo), to: iso(today)},
    }).then((res) => setCallAnalytics(res?.data))
      .catch(() => setCallAnalytics(null))
      .finally(() => setCallLoading(false))
  }, [merchantId])

  const codCfg = merchant?.codRemittanceConfig || {}
  const codOn = !!codCfg.earlyCODEnabled
  const showNdr = isAuthorized(ROLE_COMBINATIONS.NDR_ACCESS)
  const showFinance = isAuthorized(ROLE_COMBINATIONS.FINANCE_ACCESS)

  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle="NDR recovery, address & RTO-risk scoring, and early payouts — every value-added service in one place">
        Value-Added Services
      </PageTitle>

      {showNdr && (
        <>
          <SectionHeader title="Delivery Recovery" sub="Reach customers on failed or pending deliveries and turn them around." />
          <Box sx={gridSx}>
            <ServiceCard
              icon={<PhoneInTalkOutlinedIcon />} accent={PRIMARY_BLUE}
              title="NDR Calling" status={chip('Active', SUCCESS)}
              desc="Automated calls to customers on non-delivery to recover failed and pending deliveries."
              ctaLabel="View call recordings" onCta={() => navigate('/ndr/call-recordings')}
            >
              {callLoading ? (
                <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 1.5}}>Loading last 30 days…</Typography>
              ) : callAnalytics ? (
                <Box sx={{display: 'flex', gap: 4, mt: 1.5, alignItems: 'flex-end'}}>
                  <Stat value={(callAnalytics.totalCalls || 0).toLocaleString()} label="Calls · 30d" />
                  <Stat value={`${callAnalytics.connectRate || 0}%`} label="Connected" color={SUCCESS} />
                </Box>
              ) : (
                <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 1.5}}>No calls in the last 30 days.</Typography>
              )}
            </ServiceCard>

            <ServiceCard
              icon={<WhatsAppIcon />} accent={SUCCESS}
              title="NDR WhatsApp" status={chip('Active', SUCCESS)}
              desc="WhatsApp-based delivery updates and two-way NDR resolution conversations with customers."
              ctaLabel="View conversations" onCta={() => navigate('/ndr/whatsapp-conversations')}
            >
              <Bullets items={['Automated status updates to the buyer', 'Two-way chat to resolve NDRs faster']} />
            </ServiceCard>
          </Box>
        </>
      )}

      <SectionHeader title="Risk Intelligence" sub="NETRA automatically scores every order the moment it's created, so you can act before a delivery goes wrong." />
      <Box sx={gridSx}>
        <ServiceCard
          icon={<LocationOnOutlinedIcon />} accent={'#7a4fb0'}
          title="Address Verification" status={chip('NETRA', '#7a4fb0')}
          desc="How deliverable are your customers' addresses? Every address is graded Excellent → Poor."
        >
          <Bullets items={[
            'Flags incomplete or hard-to-deliver addresses before dispatch',
            'The grade for each order shows on that order’s detail page',
          ]} />
        </ServiceCard>

        <ServiceCard
          icon={<GppGoodOutlinedIcon />} accent={'#7a4fb0'}
          title="RTO Risk Score" status={chip('NETRA', '#7a4fb0')}
          desc="Which orders are likely to come back (return-to-origin)? Predicted from the buyer's history."
        >
          <Bullets items={[
            'Highlights high-risk orders so you can confirm the address or push prepaid',
            'The risk score for each order shows on that order’s detail page',
          ]} />
        </ServiceCard>
      </Box>

      {showFinance && (
        <>
          <SectionHeader title="Finance" sub="Get paid faster." />
          <Box sx={gridSx}>
            <ServiceCard
              icon={<AccountBalanceWalletOutlinedIcon />} accent={'#d99a3a'}
              title="Early COD Remittance"
              status={codOn ? chip(`Enabled${codCfg.cycleOfDays ? ` · ${codCfg.cycleOfDays}-day` : ''}`, SUCCESS) : chip('Not enabled', SUBTLE)}
              desc="Unlock working capital — receive your COD payouts ahead of the standard remittance cycle."
              ctaLabel="Manage in Billing" onCta={() => navigate('/billing')}
            >
              {!codOn && (
                <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 1.5}}>
                  Not enabled on your account yet — contact your account manager to switch on faster COD payouts.
                </Typography>
              )}
            </ServiceCard>
          </Box>
        </>
      )}
    </Box>
  )
}

export default withLayout(VasDashboard)
